<script lang="ts">
	import { getPaginatedOrderHistory } from '$lib/data/userHoldings';
	import {
		currentPageOrderHistory,
		fetchingPaginatedOrderHistory,
		paginatedOrderHistory
	} from '../store';
	import { smartWalletCreationPending, walletConnected, walletLoaded } from '$lib/wallet/store';
	import { smartContractWalletAddress } from '../scwallet/store';
	import UserOrderHistoryItem from './UserOrderHistoryItem.svelte';
	import { fly } from 'svelte/transition';
	import { quadIn } from 'svelte/easing';

	let pageNumbersToShow: Array<number | '.......' | '...'> = [1];

	function populatePageNumbersToShow() {
		if ($paginatedOrderHistory.totalPages <= 8) {
			pageNumbersToShow = Array.from(
				{ length: $paginatedOrderHistory.totalPages },
				(_, i) => i + 1
			);
		} else {
			pageNumbersToShow = [];
			pageNumbersToShow = pageNumbersToShow.concat([1, 2, 3]);

			if (
				$currentPageOrderHistory > 3 &&
				$currentPageOrderHistory < $paginatedOrderHistory.totalPages - 2
			) {
				pageNumbersToShow.push('...');
				pageNumbersToShow.push($currentPageOrderHistory);
				pageNumbersToShow.push('...');
			} else {
				pageNumbersToShow.push('.......');
			}

			pageNumbersToShow = pageNumbersToShow.concat([
				$paginatedOrderHistory.totalPages - 2,

				$paginatedOrderHistory.totalPages - 1,
				$paginatedOrderHistory.totalPages
			]);
		}
	}

	function nextPage() {
		if ($currentPageOrderHistory < $paginatedOrderHistory.totalPages)
			setCurrentPage($currentPageOrderHistory + 1);
	}

	function previousPage() {
		if ($currentPageOrderHistory > 1) setCurrentPage($currentPageOrderHistory - 1);
	}

	function setCurrentPage(val: number) {
		$currentPageOrderHistory = val;
		getPaginatedOrderHistory();
	}

	$: if ($walletConnected && $paginatedOrderHistory && $paginatedOrderHistory.orders.length > 0) {
		populatePageNumbersToShow();
	}
</script>

{#if $paginatedOrderHistory && $paginatedOrderHistory.orders && $paginatedOrderHistory.orders.length > 0}
	<div class="hidden sm:grid grid-cols-7 px-2 text-fx-grey-400 mb-6">
		<div class="text-xs">Time</div>
		<div class="text-xs">Perp</div>
		<div class="text-xs text-center">Side</div>
		<div class="text-xs text-center">Size/Amount</div>
		<div class="text-xs text-center">Order Type</div>
		<div class="text-xs text-center">Status</div>
		<div class="text-xs text-right">Fees</div>
	</div>
{/if}
<div class="overflow-auto sm:max-h-[45vh] no-scrollbar">
	{#if $walletLoaded && $walletConnected && $smartContractWalletAddress && $fetchingPaginatedOrderHistory}
		<div class="flex flex-col h-full gap-3 flex-grow">
			{#each Array.from(Array(7).keys()) as a}
				<div
					class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
				/>
			{/each}
		</div>
	{:else if $paginatedOrderHistory && $paginatedOrderHistory.orders && $paginatedOrderHistory.orders.length > 0}
		<div class="flex flex-col overflow-y">
			{#each $paginatedOrderHistory.orders as eachOrder, i}
				<div
					in:fly={{
						duration: 300 * Math.sqrt(i),
						x: 0,
						y: 30,
						opacity: 0.1,
						easing: quadIn
					}}
				>
					<UserOrderHistoryItem ord={eachOrder} />
				</div>
			{/each}
		</div>
	{:else}
		<div class="py-20 sm:py-10 flex flex-col items-center justify-center">
			{#if $walletLoaded && $walletConnected && $smartContractWalletAddress}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">No Orders Found</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					Once you place an order, the filled and executed orders will appear here before being
					filled.
				</div>
			{:else if $walletLoaded && !$walletConnected}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">Connect wallet</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					Once you place an order, the filled and executed orders will appear here before being
					filled.
				</div>
			{:else if $smartWalletCreationPending}
				<img loading="lazy" src={'empty-position.png'} alt="" class="mx-auto max-w-[30%] mb-3" />
				<div class="text-center text-fx-grey-200 font-medium text-sm mb-2">
					Create Smart Contract Wallet
				</div>
				<div class="w-full text-fx-grey-400 text-xs rounded-xl squircle-xl text-center">
					Once you place an order, the filled and executed orders will appear here before being
					filled.
				</div>
			{:else}
				<div class="grid gap-2">
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
					<div
						class="w-full h-12 bgx-fx-grey-600 dark:bg-fx-grey-700 animate-pulse rounded-xl squircle-xl"
					/>
				</div>
			{/if}
		</div>
	{/if}
</div>

{#if $paginatedOrderHistory && $paginatedOrderHistory.orders.length > 0}
	<div class="p-4 flex items-center justify-center gap-4">
		<button
			on:click={previousPage}
			on:keydown={previousPage}
			class="text-fx-grey-300 text-xs cursor-pointer hover:text-fx-white"
		>
			{'<'} Prev
		</button>
		{#each pageNumbersToShow as p}
			<button
				on:click={() => {
					if (typeof p == 'number') setCurrentPage(p);
				}}
				on:keydown={() => {
					if (typeof p == 'number') setCurrentPage(p);
				}}
				class=" text-xs cursor-pointer hover:text-fx-white"
				class:text-fx-white={$currentPageOrderHistory == p}
				class:text-fx-grey-300={$currentPageOrderHistory != p}
			>
				{p}
			</button>
		{/each}
		<button
			on:click={nextPage}
			on:keydown={nextPage}
			class="text-fx-grey-300 text-xs cursor-pointer hover:text-fx-white"
		>
			Next {'>'}
		</button>
	</div>
{/if}
